import { useFrame, extend } from '@react-three/fiber';
import React, { useRef, FunctionComponent, memo, useMemo, useEffect } from 'react';
import { Group } from 'three';
import {EmitterGroup} from '../../../threejs-particle-system/src/v1/core/EmitterGroup'
import { IEmitterGroupWrapperProps } from '../types';

// Extend so the reconciler will learn about it
extend({ EmitterGroup })

const EmitterGroupWrapper: FunctionComponent<IEmitterGroupWrapperProps> = ({
    args: a, children: emitter, position, rotation, scale, renderOrder
}) => {
    const threeGroupRef = useRef<Group>(null);
    // `a` is a fresh array literal on every render (`args={[emitterGroupSettings]}` in the callers), so it
    // is never a usable dependency. Indexing into it is: `a[0]` is the settings object memoized by the
    // caller, and therefore changes identity exactly when the emitter's group settings change.
    const args = a?.[0];

    // Build the three.js EmitterGroup from its settings, and REBUILD it when they change.
    //
    // This used to be a `useRef` initialiser, which constructs exactly once for the life of the component.
    // That made it the real blocker on live emitter edits: no dep array further up the tree could have any
    // effect while the group itself was built once and kept forever. The old group is disposed by the
    // cleanup below — `convertDocToThreeEmitterGroupSettings` loads a NEW texture per conversion, so an
    // un-disposed predecessor leaks a texture, a geometry and a material on every edit.
    const emitterGroup = useMemo(() => new EmitterGroup(args!), [args]);

    // const emitterAreaColor = emitterArea?.color;


    // Update particle group with new delta time to move particles
    useFrame((_, delta) => {
        emitterGroup?.tick(delta);
    })

    // Add emitter to emitter group, remove before adding a new one.
    // Declared BEFORE the dispose effect so that on a rebuild React runs this cleanup first and the
    // emitter is detached from the outgoing group while that group is still intact.
    useEffect(() => {
        if (!emitterGroup || !emitter) return;

        emitterGroup.addEmitter(emitter)
        return () => {
            emitterGroup.removeEmitter(emitter);
        }
    }, [emitterGroup, emitter])

    // Add group mesh to the three group; dispose and remove when the group is replaced or before unmount
    useEffect(() => {
        const threeGroup = threeGroupRef.current;
        if (!threeGroup || !emitterGroup) return;
        threeGroup.add(emitterGroup.mesh);

        return () => {
            threeGroup.remove(emitterGroup.mesh)
            emitterGroup.dispose();
        }
    }, [emitterGroup])

    // `renderOrder` is a runtime prop, so it is applied in place. It used to be a dependency of the effect
    // above, whose cleanup disposes the group — so every renderOrder change disposed the group's geometry,
    // material and texture and then re-attached the mesh. That was wasteful rather than broken (three.js
    // re-uploads disposed resources on the next render, and `dispose()` never touches the JS-side data),
    // but it was not cheap: `transitionAdjustedRenderOrder` multiplies by 1000 during a scene-enter
    // transition, so the whole particle buffer was re-uploaded to the GPU at every transition boundary.
    // Splitting it keeps disposal keyed to the group's own lifetime, which is what it was always for.
    useEffect(() => {
        if (!emitterGroup) return;
        emitterGroup.mesh.renderOrder = renderOrder;
    }, [emitterGroup, renderOrder])

    return (
        <group name='emitter-wrapper-group' ref={threeGroupRef} position={position} scale={scale} rotation={rotation} />
    );
}

export default memo(EmitterGroupWrapper);